import React, { useEffect, useState } from "react";
import { apiRequests } from "../../shared/api/api";
import { Button } from "src/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "src/components/ui/card";
import { Badge } from "src/components/ui/badge";
import { Skeleton } from "src/components/ui/skeleton";

const QrPublicLanding = ({ code }) => {
  const [page, setPage] = useState(null);
  const [visitId, setVisitId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const interStyle = { fontFamily: "Inter, sans-serif" };

  useEffect(() => {
    if (!code) return;
    const init = async () => {
      try {
        const pageRes = await apiRequests.loyaltyQr.getPublicPage(code);
        setPage(pageRes.data.page);
        const visitRes = await apiRequests.loyaltyQr.postVisit(code);
        setVisitId(visitRes.data.visit_id);
      } catch (err) {
        console.error("QR landing error:", err);
        setError("Страница не найдена");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [code]);

  const handleButtonClick = async (btn) => {
    if (!visitId) return;
    try {
      await apiRequests.loyaltyQr.postTarget(visitId, {
        button_type: btn.type,
        registered: true,
      });
      // Use bot_url if set, otherwise fallback defaults
      if (btn.bot_url) {
        window.location.href = btn.bot_url;
      } else if (btn.type === "SMS") {
        alert("Введите номер телефона (демо)");
      }
    } catch (err) {
      console.error("Target tracking error:", err);
    }
  };

  const isSVG = (s) => typeof s === "string" && s.trim().startsWith("<svg");

  // Only show buttons that have a bot_url OR are type SMS
  const visibleButtons = (page?.buttons || []).filter(
    (btn) => btn.type === "SMS" || (btn.bot_url && btn.bot_url.trim() !== "")
  );

  const getButtonClasses = (type) => {
    const base =
      "w-full font-normal transition-colors focus:ring-2 focus:ring-neutral-400";
    if (type === "Telegram") {
      return `${base} bg-white text-neutral-900 border border-neutral-300 hover:bg-neutral-900 hover:text-white`;
    }
    return `${base} bg-neutral-900 text-white hover:bg-neutral-700`;
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-neutral-100 p-4"
        style={interStyle}
      >
        <Card className="w-full max-w-sm border border-neutral-200 shadow-sm overflow-hidden">
          <Skeleton className="h-48 w-full rounded-none bg-neutral-200" />
          <div className="p-4 space-y-3 text-center">
            <div className="flex items-center justify-center gap-2">
              <Skeleton className="h-8 w-8 rounded-full bg-neutral-200" />
              <Skeleton className="h-5 w-32 bg-neutral-200" />
            </div>
            <Skeleton className="h-4 w-full bg-neutral-200" />
            <Skeleton className="h-4 w-2/3 mx-auto bg-neutral-200" />
            <Skeleton className="h-9 w-full rounded-md bg-neutral-200" />
            <Skeleton className="h-9 w-full rounded-md bg-neutral-200" />
            <Skeleton className="h-3 w-24 mx-auto bg-neutral-200" />
          </div>
        </Card>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-neutral-100 p-4"
        style={interStyle}
      >
        <Card className="w-full max-w-sm border border-neutral-200 shadow-sm">
          <CardContent className="pt-8 pb-8 text-center space-y-2">
            <p className="text-sm text-red-600">Ошибка</p>
            <p className="text-neutral-600 text-sm">
              {error || "Страница не найдена"}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-neutral-100"
      style={interStyle}
    >
      <Card className="w-full max-w-sm border border-neutral-200 shadow-sm overflow-hidden p-0">
        {page.image_url ? (
          isSVG(page.image_url) ? (
            <div
              dangerouslySetInnerHTML={{ __html: page.image_url }}
              className="h-48 w-full overflow-hidden [&_svg]:h-full [&_svg]:w-full [&_svg]:object-cover"
            />
          ) : (
            <img
              src={page.image_url}
              alt="cover"
              className="h-48 w-full object-contain"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          )
        ) : (
          <div className="h-48 w-full bg-neutral-200" />
        )}

        <CardHeader className="pb-2 pt-4 text-center">
          <div className="flex items-center justify-center gap-2">
            {page.logo &&
              (isSVG(page.logo) ? (
                <div
                  dangerouslySetInnerHTML={{ __html: page.logo }}
                  className="h-6 w-6 text-neutral-800 [&_svg]:h-full [&_svg]:w-full [&_svg]:fill-current"
                />
              ) : (
                <span className="text-2xl leading-none">{page.logo}</span>
              ))}
            <CardTitle className="text-xl text-neutral-900 font-semibold">
              {page.name}
            </CardTitle>
          </div>
          {page.description && (
            <CardDescription className="text-neutral-600 text-sm pt-1">
              {page.description}
            </CardDescription>
          )}
        </CardHeader>

        <CardContent className="space-y-4 pb-5">
          {visibleButtons.length > 0 && (
            <div className="flex flex-col gap-2 w-full">
              {visibleButtons.map((btn) => (
                <Button
                  key={btn.type}
                  onClick={() => handleButtonClick(btn)}
                  className={getButtonClasses(btn.type)}
                >
                  {btn.label}
                </Button>
              ))}
            </div>
          )}

          {page.auto_tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 justify-center">
              {page.auto_tags.map((tag) => (
                <Badge
                  key={tag}
                  variant="secondary"
                  className="bg-neutral-100 text-neutral-700 hover:bg-neutral-200 text-xs font-normal"
                >
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default QrPublicLanding;